// Port of webapp/src/components/searchDialog — Ctrl+K board/card search.
// Upstream calls POST /api/v2/boards/search; the clone calls the same endpoint
// through OctoClient and also matches card titles from the local cache.

import {ReactElement, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {Board} from '../types/board'
import {Card} from '../types/card'
import {buildBoardPath} from '../common/paths'
import client from '../api/client'
import {useTeams} from '../store/teams'
import {useBoards} from '../store/boards'
import {useCards} from '../store/cards'
import {Icon} from './common/icons'
import RootPortal from './common/rootPortal'

type Props = {
    onClose: () => void
}

type Hit = {kind: 'board' | 'card', board: Board, card?: Card}

/** Board metadata lives in its own store; read it imperatively. */
function knownBoards(): Array<[string, Board]> {
    return Object.entries(useBoards.getState().boards).filter(([, board]) => !board.isTemplate)
}

export default function SearchDialog({onClose}: Props): ReactElement {
    const navigate = useNavigate()
    const teamId = useTeams((state) => state.activeTeamId)
    const [term, setTerm] = useState('')
    const [boardHits, setBoardHits] = useState<Board[]>([])
    const cards = useCards((state) => state.cards)

    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        // deferred one task so it runs after RootPortal has (re)attached the host
        const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0)
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                onClose()
            }
        }
        document.addEventListener('keydown', onKey)
        return () => {
            window.clearTimeout(focusTimer)
            document.removeEventListener('keydown', onKey)
        }
    }, [onClose])

    useEffect(() => {
        let cancelled = false
        const timer = window.setTimeout(async () => {
            const found = term.trim() ? await client.searchBoards(term) : []
            if (!cancelled) {
                setBoardHits(found)
            }
        }, 120)
        return () => {
            cancelled = true
            window.clearTimeout(timer)
        }
    }, [term])

    const hits: Hit[] = useMemo(() => {
        const needle = term.trim().toLowerCase()
        const boardItems: Hit[] = boardHits.map((board) => ({kind: 'board', board}))
        if (!needle) {
            return boardItems
        }
        const boardsById = new Map(knownBoards())
        const cardItems: Hit[] = Object.values(cards).
            filter((card) => card.deleteAt === 0 && card.title.toLowerCase().includes(needle)).
            slice(0, 12).
            map((card): Hit | null => {
                const owner = boardsById.get(card.boardId)
                return owner ? {kind: 'card', board: owner, card} : null
            }).
            filter((hit): hit is Hit => Boolean(hit))
        return [...boardItems, ...cardItems]
    }, [boardHits, cards, term])

    const open = (hit: Hit): void => {
        onClose()
        navigate(buildBoardPath({teamId, boardId: hit.board.id, cardId: hit.card?.id}))
    }

    return (
        <RootPortal>
            <div
                className='fixed inset-0 z-dialog flex items-start justify-center bg-black/30 pt-[12vh]'
                onMouseDown={(event) => event.target === event.currentTarget && onClose()}
                role='presentation'
            >
                <div className='Dialog w-full max-w-[560px] p-0' role='dialog' aria-modal='true' aria-label='搜索'>
                    <div className='flex items-center gap-2 border-b border-main-fg/10 px-4 py-3'>
                        <Icon name='search' size={16} className='opacity-60'/>
                        <input
                            ref={inputRef}
                            className='flex-1 bg-transparent outline-none'
                            placeholder='搜索看板与卡片'
                            value={term}
                            onChange={(event) => setTerm(event.target.value)}
                            onKeyDown={(event) => event.key === 'Escape' && onClose()}
                        />
                        <kbd className='rounded border border-main-fg/20 px-1 text-xxs opacity-60'>退出键</kbd>
                    </div>
                    <div className='max-h-[45vh] overflow-auto py-2'>
                        {term.trim() && hits.length === 0 && (
                            <div className='px-4 py-6 text-center text-sm opacity-60'>没有找到与「{term}」相关的看板或卡片。</div>
                        )}
                        {!term.trim() && (
                            <div className='px-4 py-6 text-center text-sm opacity-60'>输入关键词即可搜索看板与卡片。</div>
                        )}
                        {hits.map((hit, index) => (
                            <button
                                key={`${hit.kind}-${hit.card?.id || hit.board.id}-${index}`}
                                type='button'
                                className='flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-main-fg/10'
                                onClick={() => open(hit)}
                            >
                                <span className='text-base' aria-hidden={true}>{hit.card?.fields.icon || hit.board.icon}</span>
                                <span className='min-w-0 flex-1'>
                                    <span className='block truncate text-sm font-medium'>{hit.card?.title || hit.board.title}</span>
                                    <span className='block truncate text-xs opacity-60'>
                                        {hit.kind === 'card' ? `卡片 · ${hit.board.title}` : '看板'}
                                    </span>
                                </span>
                                <Icon name='chevron-right' size={14} className='opacity-40'/>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </RootPortal>
    )
}
